import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { getRiskBadgeClass } from '../utils/helpers';
import { Save, CheckCircle, Camera, MessageSquare, AlertTriangle, ChevronLeft, X, FolderOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConductAudit = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePhoto, setActivePhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const fetchAudit = useCallback(async () => {
    try {
      const auditRes = await axios.get(`${API}/audits/${auditId}`);
      setAudit(auditRes.data);
      const templateRes = await axios.get(`${API}/audit-templates/${auditRes.data.audit_type}`);
      setTemplate(templateRes.data);
      const respMap = {};
      auditRes.data.responses.forEach(r => { respMap[r.question_id] = r; });
      setResponses(respMap);
    } catch (error) {
      toast.error('Failed to load audit');
    } finally {
      setLoading(false);
    }
  }, [auditId]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const updateResponse = (questionId, field, value) => {
    setResponses(prev => ({ ...prev, [questionId]: { ...prev[questionId], [field]: value } }));
  };

  const handlePhotoUpload = async (questionId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const currentPhotos = responses[questionId]?.photos || [];
      updateResponse(questionId, 'photos', [...currentPhotos, response.data.url]);
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
      setActivePhoto(null);
      // Reset input values
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const openCamera = (questionId) => {
    setActivePhoto(questionId);
    cameraInputRef.current?.click();
  };

  const openGallery = (questionId) => {
    setActivePhoto(questionId);
    galleryInputRef.current?.click();
  };

  const removePhoto = (questionId, photoIndex) => {
    const currentPhotos = responses[questionId]?.photos || [];
    updateResponse(questionId, 'photos', currentPhotos.filter((_, i) => i !== photoIndex));
  };

  const saveAudit = async (complete = false) => {
    setSaving(true);
    try {
      const responsesList = Object.entries(responses).map(([questionId, data]) => ({
        question_id: questionId,
        obtained_mark: data.obtained_mark || 0,
        is_na: data.is_na || false,
        comments: data.comments || '',
        photos: data.photos || [],
        requires_corrective_action: data.requires_corrective_action || false
      }));
      await axios.put(`${API}/audits/${auditId}`, { responses: responsesList, status: complete ? 'completed' : 'in_progress' });
      toast.success(complete ? 'Audit completed!' : 'Progress saved');
      if (complete) { navigate(`/audits/${auditId}`); } else { fetchAudit(); }
    } catch (error) {
      toast.error('Failed to save audit');
    } finally {
      setSaving(false);
    }
  };

  const calculateCategoryScore = (categoryQuestions) => {
    let obtained = 0, max = 0;
    categoryQuestions.forEach(q => {
      // Skip NA questions from score calculation
      if (!responses[q.id]?.is_na) {
        max += q.max_score;
        obtained += responses[q.id]?.obtained_mark || 0;
      }
    });
    return { obtained, max, percentage: max > 0 ? Math.round((obtained / max) * 100) : 0 };
  };

  const calculateTotalProgress = () => {
    if (!template) return 0;
    let answered = 0, total = 0;
    Object.values(template.categories).forEach(questions => {
      total += questions.length;
      questions.forEach(q => {
        // Count as answered if: has score > 0, has comment, or is marked NA
        if (responses[q.id]?.obtained_mark > 0 || responses[q.id]?.comments || responses[q.id]?.is_na) answered++;
      });
    });
    return total > 0 ? Math.round((answered / total) * 100) : 0;
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;
  if (!audit || !template) return <div className="text-center py-12"><p className="text-slate-500">Audit not found</p><Button onClick={() => navigate('/audits')} className="mt-4">Go to Audits</Button></div>;

  const progress = calculateTotalProgress();
  const categoryKeys = Object.keys(template.categories);

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="conduct-audit-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/audits')} data-testid="back-btn"><ChevronLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900">{audit.site_name}</h1>
          <p className="text-slate-500">{audit.site_location} • {audit.audit_type === 'central_kitchen' ? 'Central Kitchen' : 'Site'} Audit</p>
        </div>
      </div>

      <Card data-testid="progress-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div><p className="text-sm font-medium text-slate-500">Audit Progress</p><p className="text-3xl font-bold font-['Chivo'] text-slate-900">{progress}%</p></div>
            <div className="text-right"><p className="text-sm font-medium text-slate-500">Current Score</p><p className="text-3xl font-bold font-['Chivo'] text-slate-900">{audit.score_percentage}%</p></div>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => saveAudit(false)} disabled={saving} className="flex items-center gap-2" data-testid="save-progress-btn"><Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Progress'}</Button>
            <Button onClick={() => saveAudit(true)} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="complete-audit-btn"><CheckCircle className="w-4 h-4" />Complete Audit</Button>
          </div>
        </CardContent>
      </Card>

      {/* Hidden file inputs for camera and gallery */}
      <input type="file" accept="image/*" capture="environment" className="hidden" ref={cameraInputRef} onChange={(e) => handlePhotoUpload(activePhoto, e)} />
      <input type="file" accept="image/*" className="hidden" ref={galleryInputRef} onChange={(e) => handlePhotoUpload(activePhoto, e)} />

      <Accordion type="single" collapsible className="space-y-4" data-testid="categories-accordion">
        {categoryKeys.map(category => {
          const questions = template.categories[category];
          const catScore = calculateCategoryScore(questions);
          return (
            <AccordionItem key={category} value={category} className="border rounded-lg bg-white shadow-sm">
              <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid={`category-${category}`}>
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-slate-900">{category}</span>
                    <Badge variant="outline" className="text-xs">{questions.length} items</Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-500">{catScore.obtained}/{catScore.max}</span>
                    <Badge className={catScore.percentage >= 90 ? 'bg-emerald-100 text-emerald-700' : catScore.percentage >= 80 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>{catScore.percentage}%</Badge>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">
                <div className="space-y-4">
                  {questions.map((q, idx) => (
                    <QuestionRow 
                      key={q.id} 
                      question={q} 
                      index={idx} 
                      response={responses[q.id]} 
                      onUpdateResponse={updateResponse} 
                      onOpenCamera={() => openCamera(q.id)}
                      onOpenGallery={() => openGallery(q.id)}
                      uploading={uploading && activePhoto === q.id} 
                      onRemovePhoto={(pi) => removePhoto(q.id, pi)} 
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <div className="sticky bottom-4 flex justify-center">
        <Card className="shadow-lg">
          <CardContent className="p-4 flex gap-3">
            <Button variant="outline" onClick={() => saveAudit(false)} disabled={saving} className="flex items-center gap-2"><Save className="w-4 h-4" />Save Progress</Button>
            <Button onClick={() => saveAudit(true)} disabled={saving} className="btn-primary flex items-center gap-2"><CheckCircle className="w-4 h-4" />Complete Audit</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

function QuestionRow({ question, index, response, onUpdateResponse, onOpenCamera, onOpenGallery, uploading, onRemovePhoto }) {
  const obtainedMark = response?.obtained_mark ?? null;
  const isNA = response?.is_na || false;
  const comments = response?.comments || '';
  const photos = response?.photos || [];
  const requiresAction = response?.requires_corrective_action || false;

  // For Critical items, only show 0 and 5 (binary scoring)
  const isCritical = question.risk === 'Critical';
  const scores = isCritical ? [0, 5] : [];
  if (!isCritical) {
    for (let i = 0; i <= question.max_score; i++) { scores.push(i); }
  }

  const handleNAToggle = () => {
    if (isNA) {
      // Turning NA off - reset to score 0
      onUpdateResponse(question.id, 'is_na', false);
      onUpdateResponse(question.id, 'obtained_mark', 0);
    } else {
      // Turning NA on - clear score
      onUpdateResponse(question.id, 'is_na', true);
      onUpdateResponse(question.id, 'obtained_mark', 0);
    }
  };

  return (
    <div className={`checklist-item ${isNA ? 'opacity-60' : ''}`} data-testid={`question-${question.id}`}>
      <div className="flex items-start gap-4">
        <span className="text-sm font-medium text-slate-400 min-w-[24px]">{index + 1}.</span>
        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <p className={`text-sm leading-relaxed ${isNA ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{question.question}</p>
            <div className="flex items-center gap-2 shrink-0">
              {isNA && <Badge className="bg-gray-100 text-gray-600">N/A</Badge>}
              <Badge className={`${getRiskBadgeClass(question.risk)}`}>{question.risk}</Badge>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 min-w-[60px]">Score:</span>
            <div className="flex items-center gap-2">
              {scores.map(s => (
                <Button 
                  key={s} 
                  type="button" 
                  size="sm" 
                  variant={!isNA && obtainedMark === s ? 'default' : 'outline'} 
                  className={`w-10 h-10 ${!isNA && obtainedMark === s ? 'bg-slate-900 text-white' : ''}`} 
                  onClick={() => {
                    if (isNA) {
                      onUpdateResponse(question.id, 'is_na', false);
                    }
                    onUpdateResponse(question.id, 'obtained_mark', s);
                  }} 
                  disabled={isNA}
                  data-testid={`score-${question.id}-${s}`}
                >
                  {s}
                </Button>
              ))}
              <Button
                type="button"
                size="sm"
                variant={isNA ? 'default' : 'outline'}
                className={`w-12 h-10 ${isNA ? 'bg-gray-600 text-white' : 'text-gray-500'}`}
                onClick={handleNAToggle}
                data-testid={`na-btn-${question.id}`}
              >
                NA
              </Button>
              <span className="text-sm text-slate-400 ml-2">/ {question.max_score}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className={`flex items-center gap-2 ${comments ? 'bg-blue-50 border-blue-200' : ''}`} data-testid={`comment-btn-${question.id}`}>
                  <MessageSquare className="w-4 h-4" />{comments ? 'Edit Comment' : 'Add Comment'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add Comment</DialogTitle><DialogDescription>{question.question}</DialogDescription></DialogHeader>
                <div className="mt-4"><Textarea placeholder="Enter your observations..." value={comments} onChange={(e) => onUpdateResponse(question.id, 'comments', e.target.value)} rows={4} data-testid={`comment-textarea-${question.id}`} /></div>
              </DialogContent>
            </Dialog>
            
            {/* Photo Upload Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className={`flex items-center gap-2 ${photos.length > 0 ? 'bg-green-50 border-green-200' : ''}`} disabled={uploading} data-testid={`photo-btn-${question.id}`}>
                  <Camera className="w-4 h-4" />{uploading ? 'Uploading...' : photos.length > 0 ? `${photos.length} Photo(s)` : 'Add Photo'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem onClick={onOpenCamera} className="cursor-pointer" data-testid={`camera-option-${question.id}`}>
                  <Camera className="w-4 h-4 mr-2" />
                  Take Photo
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onOpenGallery} className="cursor-pointer" data-testid={`gallery-option-${question.id}`}>
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Choose from Gallery
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {obtainedMark < question.max_score && (
              <Button variant="outline" size="sm" className={`flex items-center gap-2 ${requiresAction ? 'bg-red-50 border-red-200 text-red-700' : ''}`} onClick={() => onUpdateResponse(question.id, 'requires_corrective_action', !requiresAction)} data-testid={`action-btn-${question.id}`}>
                <AlertTriangle className="w-4 h-4" />{requiresAction ? 'Action Required' : 'Flag for Action'}
              </Button>
            )}
          </div>
          {photos.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {photos.map((photo, pi) => (
                <div key={pi} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${photo}`} alt={`Evidence ${pi + 1}`} className="w-full h-full object-cover" />
                  <button type="button" className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-bl" onClick={() => onRemovePhoto(pi)}><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConductAudit;
